import Modal from 'antd/lib/modal/Modal';
import Button from 'antd/lib/button';
import Form from 'antd/lib/form';
import Typography from 'antd/lib/typography';
import Radio from 'antd/lib/radio';
import React from 'react';
import { useLocalStorage } from '../../../domain/useLocalStorage';
import { useAuth } from '../../../domain/auth/useAuth';
import './download-event-performance-modal.css';

export interface IDownloadEventPerformanceCsvFormData {
    applyFilters: boolean;
    groupByBatch: boolean;
}

interface IDownloadEventPerformanceCsvModal {
    allowGroupByBatch: boolean;
    onClose: () => void;
    onSubmit: (data: IDownloadEventPerformanceCsvFormData) => void;
}

const STORAGE_KEY = 'event-performance-csv-download-options';

const DEFAULT_FORM_VALUE: IDownloadEventPerformanceCsvFormData = {
    applyFilters: true,
    groupByBatch: false,
};

export const DownloadEventPerformanceCsvModal = ({ onClose, onSubmit, allowGroupByBatch }: IDownloadEventPerformanceCsvModal) => {
    const [form] = Form.useForm<IDownloadEventPerformanceCsvFormData>();
    const auth = useAuth()!;
    const [options, setOptions] = useLocalStorage<IDownloadEventPerformanceCsvFormData>(`${STORAGE_KEY}-${auth.user?.user_id}`, DEFAULT_FORM_VALUE);

    const handleClose = () => onClose();

    const handleConfirm = (formData: IDownloadEventPerformanceCsvFormData) => {
        setOptions(formData);
        onSubmit(formData);
    };

    return (
        <Modal
            className="download-event-performance-modal"
            open
            title="Download Estimated Event Performance CSV"
            destroyOnHidden
            closeIcon
            onCancel={handleClose}
            styles={{
                footer: {
                    borderTop: 'none',
                    padding: '0 24px 24px 24px',
                },
            }}
            footer={[
                <Button key="modal-cancel" onClick={handleClose}>
                    Cancel
                </Button>,
                <Button key="download-csv-confirm" type="primary" onClick={form.submit}>
                    Download
                </Button>,
            ]}
        >
            <Form
                form={form}
                name="download-event-performance-csv-form"
                preserve={false}
                layout="vertical"
                onFinish={handleConfirm}
                style={{ marginTop: '12px' }}
                initialValues={{ ...DEFAULT_FORM_VALUE, ...options }}
            >
                <Form.Item
                    name="applyFilters"
                    label={<Typography.Text strong>Filters</Typography.Text>}
                    rules={[{ required: true, message: 'Please select a filter option' }]}
                >
                    <Radio.Group
                        options={[
                            {
                                value: true,
                                className: 'event-analytics-custom-radio-button-wrapper',
                                label: (
                                    <>
                                        <Typography.Text strong>Apply Filters</Typography.Text>
                                        <br />
                                        <Typography.Text type="secondary">Download will only include filtered items</Typography.Text>
                                    </>
                                ),
                            },
                            {
                                value: false,
                                className: 'event-analytics-custom-radio-button-wrapper',
                                label: (
                                    <>
                                        <Typography.Text strong>Download All</Typography.Text>
                                        <br />
                                        <Typography.Text type="secondary">All event performance data will be included</Typography.Text>
                                    </>
                                ),
                            },
                        ]}
                    />
                </Form.Item>

                {allowGroupByBatch && (
                    <Form.Item
                        name="groupByBatch"
                        label={<Typography.Text strong>Data Aggregation</Typography.Text>}
                        rules={[{ required: true, message: 'Please select a filter option' }]}
                    >
                        <Radio.Group
                            options={[
                                {
                                    value: true,
                                    className: 'event-analytics-custom-radio-button-wrapper',
                                    label: (
                                        <>
                                            <Typography.Text strong>One row per DRMS Event (batch)</Typography.Text>
                                            <br />
                                            <Typography.Text type="secondary">
                                                All event performance data combined in one row as on UI
                                            </Typography.Text>
                                        </>
                                    ),
                                },
                                {
                                    value: false,
                                    className: 'event-analytics-custom-radio-button-wrapper',
                                    label: (
                                        <>
                                            <Typography.Text strong>One row per Meter</Typography.Text>
                                            <br />
                                            <Typography.Text type="secondary">
                                                Each row contains performance data for individual meter
                                            </Typography.Text>
                                        </>
                                    ),
                                },
                            ]}
                        />
                    </Form.Item>
                )}
            </Form>
        </Modal>
    );
};
